import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TABS = ["Website", "Notifications", "Account"];

export default function SettingsPage() {
  const [tab, setTab] = useState("Website");
  return (
    <div className="space-y-4" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-display font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 text-sm">Website, notifications and your account</p>
      </div>
      <div className="flex gap-1">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${tab === t ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`settings-tab-${t.toLowerCase()}`}>{t}</button>
        ))}
      </div>
      {tab === "Website" && <WebsiteTab />}
      {tab === "Notifications" && <NotificationsTab />}
      {tab === "Account" && <AccountTab />}
    </div>
  );
}

function WebsiteTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings/website").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-slate-500">Loading...</div>;
  const upd = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setFees = (nf) => setS((p) => ({ ...p, consultation_fees: nf, consultation_types: nf.map((x) => x.type) }));

  async function save() {
    try {
      const payload = { ...s };
      delete payload.key; delete payload.updated_at;
      await api.put("/settings/website", payload);
      toast.success("Website settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <F label="Doctor name"><input className="input" value={s.doctor_name || ""} onChange={(e) => upd("doctor_name", e.target.value)} data-testid="settings-doctor-name" /></F>
        <F label="Title"><input className="input" value={s.doctor_title || ""} onChange={(e) => upd("doctor_title", e.target.value)} /></F>
        <F label="Tagline"><input className="input" value={s.tagline || ""} onChange={(e) => upd("tagline", e.target.value)} /></F>
        <F label="Years of experience"><input type="number" className="input" value={s.years_experience || 0} onChange={(e) => upd("years_experience", Number(e.target.value))} /></F>
        <F label="Photo URL" className="md:col-span-2"><input className="input" value={s.photo_url || ""} onChange={(e) => upd("photo_url", e.target.value)} /></F>
        <F label="Bio" className="md:col-span-2"><textarea rows={4} className="input" value={s.bio || ""} onChange={(e) => upd("bio", e.target.value)} /></F>
        <F label="Qualifications (one per line)"><textarea rows={4} className="input" value={(s.qualifications || []).join("\n")} onChange={(e) => upd("qualifications", e.target.value.split("\n").filter(Boolean))} /></F>
        <F label="Specialities (one per line)"><textarea rows={4} className="input" value={(s.specialities || []).join("\n")} onChange={(e) => upd("specialities", e.target.value.split("\n").filter(Boolean))} /></F>
        <F label="Clinic name"><input className="input" value={s.clinic_name || ""} onChange={(e) => upd("clinic_name", e.target.value)} /></F>
        <F label="Clinic address"><input className="input" value={s.clinic_address || ""} onChange={(e) => upd("clinic_address", e.target.value)} /></F>
        <F label="City"><input className="input" value={s.clinic_city || ""} onChange={(e) => upd("clinic_city", e.target.value)} /></F>
        <F label="Map embed URL"><input className="input" value={s.clinic_map_url || ""} onChange={(e) => upd("clinic_map_url", e.target.value)} /></F>
        <F label="Phone"><input className="input" value={s.phone || ""} onChange={(e) => upd("phone", e.target.value)} /></F>
        <F label="WhatsApp"><input className="input" value={s.whatsapp || ""} onChange={(e) => upd("whatsapp", e.target.value)} /></F>
        <F label="Clinic email"><input className="input" value={s.email || ""} onChange={(e) => upd("email", e.target.value)} /></F>
        <F label="Languages (comma-separated)"><input className="input" value={(s.languages || []).join(", ")} onChange={(e) => upd("languages", e.target.value.split(",").map(x => x.trim()).filter(Boolean))} /></F>
        <F label="Primary color"><input className="input" value={s.primary_color || ""} onChange={(e) => upd("primary_color", e.target.value)} /></F>
        <F label="Accent color"><input className="input" value={s.accent_color || ""} onChange={(e) => upd("accent_color", e.target.value)} /></F>
        <label className="flex items-center gap-2 md:col-span-2 mt-2">
          <input type="checkbox" checked={!!s.requires_approval} onChange={(e) => upd("requires_approval", e.target.checked)} />
          <span className="text-sm font-medium text-slate-800">Appointments require manual confirmation (shows as "Pending")</span>
        </label>
      </div>
      <div>
        <div className="text-xs text-slate-500 font-semibold mb-2">Services</div>
        {(s.services || []).map((svc, i) => (
          <div key={i} className="grid md:grid-cols-5 gap-2 mb-2">
            <input className="input md:col-span-2" value={svc.name || ""} onChange={(e) => { const sv = [...s.services]; sv[i] = { ...svc, name: e.target.value }; upd("services", sv); }} />
            <input className="input md:col-span-2" value={svc.description || ""} onChange={(e) => { const sv = [...s.services]; sv[i] = { ...svc, description: e.target.value }; upd("services", sv); }} />
            <button onClick={() => upd("services", s.services.filter((_, j) => j !== i))} className="text-rose-600 text-sm">Remove</button>
          </div>
        ))}
        <button onClick={() => upd("services", [...(s.services || []), { name: "New service", description: "" }])} className="text-sm text-emerald-700 font-medium">+ Add service</button>
      </div>
      <div>
        <div className="text-xs text-slate-500 font-semibold mb-2">Consultation types &amp; fees</div>
        {(s.consultation_fees || []).map((f, i) => (
          <div key={i} className="grid md:grid-cols-5 gap-2 mb-2">
            <input className="input md:col-span-2" value={f.type || ""} onChange={(e) => { const nf = [...s.consultation_fees]; nf[i] = { ...f, type: e.target.value }; setFees(nf); }} />
            <input className="input" type="number" value={f.amount || 0} onChange={(e) => { const nf = [...s.consultation_fees]; nf[i] = { ...f, amount: Number(e.target.value) }; upd("consultation_fees", nf); }} />
            <input className="input" value={f.currency || "INR"} onChange={(e) => { const nf = [...s.consultation_fees]; nf[i] = { ...f, currency: e.target.value }; upd("consultation_fees", nf); }} />
            <button onClick={() => { setFees(s.consultation_fees.filter((_, j) => j !== i)); }} className="text-rose-600 text-sm">Remove</button>
          </div>
        ))}
        <button onClick={() => { setFees([...(s.consultation_fees || []), { type: "New type", amount: 0, currency: "INR" }]); }} className="text-sm text-emerald-700 font-medium">+ Add type</button>
      </div>
      <button onClick={save} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-5 py-2.5 font-semibold text-sm" data-testid="save-website-settings-btn">Save website</button>
      <style>{`.input{width:100%;padding:.55rem .8rem;border:1px solid #CBD5E1;border-radius:.6rem;background:#fff;color:#0F172A;font-size:.9rem;}
      .input:focus{outline:none;border-color:#0D9488;box-shadow:0 0 0 3px rgb(13 148 136 / 0.15)}`}</style>
    </div>
  );
}

function F({ label, children, className = "" }) {
  return <label className={`block ${className}`}><div className="text-xs text-slate-500 font-medium mb-1">{label}</div>{children}</label>;
}

function NotificationsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings/notifications").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-slate-500">Loading...</div>;
  async function save() {
    try {
      await api.put("/settings/notifications", {
        email_enabled: s.email_enabled, email_on_booking: s.email_on_booking,
        email_on_confirm: s.email_on_confirm, email_on_reschedule: s.email_on_reschedule,
        email_on_cancel: s.email_on_cancel,
      });
      toast.success("Notifications saved");
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="notifications-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold text-slate-800">Email notifications</div>
          <div className="text-xs text-slate-500">Provider: {s.email_provider_configured ? "Configured ✓" : "Not configured"}</div>
        </div>
        <label className="flex items-center gap-2"><input type="checkbox" checked={!!s.email_enabled} onChange={(e) => setS({ ...s, email_enabled: e.target.checked })} data-testid="email-enabled-toggle" /><span className="text-sm">Enabled</span></label>
      </div>
      {[
        ["email_on_booking", "Email when a booking is received"],
        ["email_on_confirm", "Email when appointment is confirmed"],
        ["email_on_reschedule", "Email when rescheduled"],
        ["email_on_cancel", "Email when cancelled"],
      ].map(([k, l]) => (
        <label key={k} className="flex items-center gap-2 text-sm text-slate-800"><input type="checkbox" checked={!!s[k]} onChange={(e) => setS({ ...s, [k]: e.target.checked })} />{l}</label>
      ))}
      <div className="pt-3 border-t border-slate-200">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-800">WhatsApp notifications</span>
          <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700">Not configured — MVP</span>
        </div>
        <p className="text-xs text-slate-500 mt-1">WhatsApp Business API integration will be configured in a future deployment. Appointments can be managed manually in the meantime.</p>
      </div>
      <button onClick={save} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-5 py-2.5 font-semibold text-sm" data-testid="save-notifications-btn">Save</button>
    </div>
  );
}

function AccountTab() {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [logs, setLogs] = useState([]);
  const loadLogs = () => api.get("/audit-logs").then((r) => setLogs(r.data));
  useEffect(() => { loadLogs(); }, []);
  async function change(e) {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: np });
      toast.success("Password updated");
      setCur(""); setNp(""); loadLogs();
    } catch (err) { toast.error(formatApiError(err)); }
  }
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="font-semibold text-slate-800">Account</div>
        <div className="text-sm text-slate-600 mt-1">{user?.email}</div>
        <form onSubmit={change} className="mt-4 grid sm:grid-cols-3 gap-2 max-w-xl">
          <input type="password" required placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} className="input" data-testid="current-password-input" />
          <input type="password" required minLength={6} placeholder="New password" value={np} onChange={(e) => setNp(e.target.value)} className="input" data-testid="new-password-input" />
          <button className="bg-slate-900 text-white rounded-lg px-4 text-sm font-semibold" data-testid="change-password-btn">Update</button>
        </form>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="font-semibold text-slate-800 mb-3">Audit log</div>
        <div className="space-y-1 max-h-80 overflow-y-auto text-xs">
          {logs.map((l) => (
            <div key={l.id} className="text-slate-600 py-1 border-b border-slate-100 last:border-0">
              <span className="font-mono">{new Date(l.created_at).toLocaleString()}</span> · <span className="font-semibold">{l.action}</span> · {l.actor}{l.target ? ` · ${l.target}` : ""}
            </div>
          ))}
          {logs.length === 0 && <div className="text-slate-500">No activity yet.</div>}
        </div>
      </div>
      <style>{`.input{width:100%;padding:.55rem .8rem;border:1px solid #CBD5E1;border-radius:.6rem;background:#fff;color:#0F172A;font-size:.9rem;}
      .input:focus{outline:none;border-color:#0D9488;box-shadow:0 0 0 3px rgb(13 148 136 / 0.15)}`}</style>
    </div>
  );
}
